import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, ExternalLink, Calendar, Scale, BookOpen, Loader2 } from "lucide-react";
import { getPublicNewsPost } from "@/lib/api/news.functions";
import { getFullNewsContent } from "@/lib/api/news-full.functions";
import { FacebookShareButton } from "@/components/site/FacebookShareButton";
import { useSocialDwellTracker } from "@/lib/use-social-dwell-tracker";
import { LEGISLATION } from "@/lib/legislation-library";

function splitBodyAndAttribution(raw: string): {
  paragraphs: string[];
  attribution: string | null;
} {
  const parts = raw
    .split(/\n\s*\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  let attribution: string | null = null;
  if (parts.length) {
    const last = parts[parts.length - 1];
    const m = last.match(/(^|\n)\s*Source:\s*(.+)$/i);
    if (m) {
      attribution = m[2].trim();
      const stripped = last.replace(/(^|\n)\s*Source:\s*.+$/i, "").trim();
      if (stripped) parts[parts.length - 1] = stripped;
      else parts.pop();
    }
  }
  return { paragraphs: parts, attribution };
}

function detectLegislation(text: string): { title: string; short?: string }[] {
  const hits = new Map<string, { title: string; short?: string }>();
  for (const act of LEGISLATION) {
    const needles = [act.title, act.short].filter(Boolean) as string[];
    for (const n of needles) {
      if (n.length < 4) continue;
      const re = new RegExp(`\\b${n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
      if (re.test(text)) {
        hits.set(act.title, { title: act.title, short: act.short });
        break;
      }
    }
  }
  return [...hits.values()].slice(0, 6);
}

/**
 * Build a share caption with at least 3 short paragraphs so readers
 * get meaningful context on Facebook (which no longer accepts a
 * pre-filled message via URL — this is what we copy to clipboard).
 */
function buildShareCaption(post: {
  title: string;
  excerpt?: string | null;
  body?: string | null;
  source_name?: string | null;
}): string {
  const MIN_PARAS = 3;
  const MAX_PARAS = 4;
  const PARA_MAX_CHARS = 420;

  const { paragraphs } = splitBodyAndAttribution((post.body ?? "").toString());

  const clean = paragraphs
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  const intro = (post.excerpt ?? "").trim();
  const parts: string[] = [];

  // Use excerpt as lead paragraph when it isn't already the first body paragraph.
  if (intro && !(clean[0] && clean[0].toLowerCase().startsWith(intro.slice(0, 60).toLowerCase()))) {
    parts.push(intro);
  }

  for (const p of clean) {
    if (parts.length >= MAX_PARAS) break;
    parts.push(p);
  }

  // Last-resort fallback: split intro into sentences if we still have nothing.
  if (parts.length === 0 && intro) {
    parts.push(intro);
  }

  // Pad up to MIN_PARAS by splitting the longest paragraph on sentence boundaries.
  while (parts.length < MIN_PARAS && parts.some((p) => /[.!?]\s+\S/.test(p))) {
    let idx = 0;
    for (let i = 1; i < parts.length; i++) {
      if (parts[i].length > parts[idx].length) idx = i;
    }
    const sentences = parts[idx].match(/[^.!?]+[.!?]+(\s|$)/g);
    if (!sentences || sentences.length < 2) break;
    const half = Math.ceil(sentences.length / 2);
    const a = sentences.slice(0, half).join("").trim();
    const b = sentences.slice(half).join("").trim();
    if (!a || !b) break;
    parts.splice(idx, 1, a, b);
  }

  // Clamp each paragraph so the whole caption stays reasonable.
  const clamped = parts.slice(0, MAX_PARAS).map((p) => {
    if (p.length <= PARA_MAX_CHARS) return p;
    const cut = p.slice(0, PARA_MAX_CHARS);
    const stop = Math.max(
      cut.lastIndexOf(". "),
      cut.lastIndexOf("! "),
      cut.lastIndexOf("? "),
    );
    return stop > 120 ? cut.slice(0, stop + 1) : cut.replace(/\s+\S*$/, "") + "…";
  });

  const attribution = post.source_name
    ? `Source: ${post.source_name}`
    : null;

  return [post.title, ...clamped, attribution].filter(Boolean).join("\n\n");
}



const DEFAULT_SHARE_IMAGE =
  "https://nkadimengdannylcs.com/og/news-default.jpg";
/**
 * Consistent, readable 1200×630 branded thumbnails so every news story
 * has a proper Facebook / Twitter preview even when no cover was uploaded.
 * Keyed to the news categories in `src/lib/api/news.functions.ts`.
 */
const CATEGORY_SHARE_IMAGES: Record<string, string> = {
  labour: "https://nkadimengdannylcs.com/og/news-labour.jpg",
  contracts: "https://nkadimengdannylcs.com/og/news-contracts.jpg",
  compliance: "https://nkadimengdannylcs.com/og/news-compliance.jpg",
  consumer: "https://nkadimengdannylcs.com/og/news-consumer.jpg",
  general: DEFAULT_SHARE_IMAGE,
};
function fallbackImageFor(category?: string | null): string {
  const key = (category ?? "").toString().toLowerCase().trim();
  return CATEGORY_SHARE_IMAGES[key] ?? DEFAULT_SHARE_IMAGE;
}
const IMAGE_EXT_RE = /\.(?:jpe?g|png|webp|gif|avif|bmp|svg)(?:$|[?#])/i;

function hashString(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36);
}

function withCacheBust(url: string, seed: string): string {
  const v = hashString(seed).slice(0, 8);
  return url.includes("?") ? `${url}&v=${v}` : `${url}?v=${v}`;
}

/** Convert a cover URL to a valid social preview image. */
function toShareImage(url: string | null | undefined): string | null {
  if (!url) return null;
  const s = url.trim();
  if (!s) return null;

  // YouTube (embed / watch / short) → thumbnail
  const yt = s.match(
    /(?:youtube\.com\/(?:embed\/|watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
  );
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/maxresdefault.jpg`;

  // Vimeo — use vumbnail.com static thumbnail service
  const vimeo = s.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return `https://vumbnail.com/${vimeo[1]}.jpg`;

  // Anything else must be an absolute http(s) URL
  if (!/^https?:\/\//i.test(s)) return null;

  // Direct image (by extension) — use as-is
  if (IMAGE_EXT_RE.test(s)) return s;

  // Supabase storage / render endpoints without an extension → still image
  if (/\/storage\/v1\/(?:object|render\/image)\//i.test(s)) return s;

  // Common CDN/image hosts even without an extension
  if (
    /(?:images?\.unsplash\.com|res\.cloudinary\.com|imagekit\.io|imagedelivery\.net|cdn\.sanity\.io|images\.pexels\.com|i\.imgur\.com|substackcdn\.com|wp\.com|wordpress\.com|gravatar\.com|googleusercontent\.com|ggpht\.com)/i.test(
      s,
    )
  ) {
    return s;
  }

  // Unknown non-image URL (e.g. article page, iframe embed) — skip
  return null;
}

export const Route = createFileRoute("/news/$slug")({
  loader: async ({ params }) => {
    const data = await getPublicNewsPost({ data: { slug: params.slug } });
    if (!data.post) throw notFound();
    return data;
  },
  head: ({ loaderData, params }) => {
    const p: any = (loaderData as any)?.post;
    if (!p) return { meta: [{ title: "News" }] };
    const raw = (p.cover_image_url ?? "").toString();
    const resolved = toShareImage(raw) ?? fallbackImageFor(p.category);
    const seed = `${raw}|${p.category ?? ""}|${p.updated_at ?? p.published_at ?? p.id ?? ""}`;
    const shareImage = withCacheBust(resolved, seed);
    const canonical = `https://nkadimengdannylcs.com/news/${params.slug}`;

    // Build a rich share description (Facebook shows ~300 chars in the
    // link preview). This lets readers get the gist of the story straight
    // from Facebook without opening the site.
    const { paragraphs } = splitBodyAndAttribution((p.body ?? "").toString());
    const bodyText = paragraphs.join(" ").replace(/\s+/g, " ").trim();
    const longDesc = (() => {
      const base = [p.excerpt, bodyText].filter(Boolean).join(" ").trim();
      if (!base) return p.title;
      if (base.length <= 300) return base;
      const cut = base.slice(0, 300);
      const stop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
      return (stop > 160 ? cut.slice(0, stop + 1) : cut.replace(/\s+\S*$/, "")) + "…";
    })();

    // Search-result meta description must stay within 50-160 chars and give
    // the searcher a reason to click, not just the first sentence of the copy.
    const metaDesc = (() => {
      const hook = " What it means for South Africans — plain-language breakdown.";
      const budget = 160 - hook.length;
      let lead = longDesc;
      if (lead.length > budget) {
        const cut = lead.slice(0, budget - 1);
        const stop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
        lead = (stop > 50 ? cut.slice(0, stop + 1) : cut.replace(/\s+\S*$/, "")) + "…";
      }
      const out = `${lead.trim()}${hook}`;
      return out.length <= 160 ? out : lead.trim();
    })();

    // Title: keep the headline itself under ~60 chars in the SERP by dropping
    // the long brand suffix on long headlines.
    const seoTitle = (() => {
      const suffixLong = " | News — Nkadimeng Danny Legal";
      const suffixShort = " | NDLCS News";
      const t = String(p.title ?? "").trim();
      if (t.length + suffixLong.length <= 60) return t + suffixLong;
      if (t.length + suffixShort.length <= 60) return t + suffixShort;
      return t.length <= 60 ? t : t.slice(0, 57).replace(/\s+\S*$/, "") + "…";
    })();

    const articleBody = bodyText || p.excerpt || p.title;
    const published = p.published_at ? new Date(p.published_at).toISOString() : undefined;
    const modified = p.updated_at ? new Date(p.updated_at).toISOString() : published;
    const author = p.source_name || "Nkadimeng Danny Legal & Compliance Support";

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: p.title,
      description: metaDesc,
      image: [shareImage],
      datePublished: published,
      dateModified: modified,
      author: { "@type": "Organization", name: author },
      publisher: {
        "@type": "Organization",
        name: "Nkadimeng Danny Legal & Compliance Support",
        logo: {
          "@type": "ImageObject",
          url: "https://nkadimengdannylcs.com/__l5e/assets-v1/e095051d-6242-4d2a-a72e-391f32b57343/danny-main-logo.jpg",
        },
      },
      mainEntityOfPage: canonical,
      articleBody,
      articleSection: p.category ?? "Legal News",
    };

    return {
      meta: [
        { title: seoTitle },
        { name: "description", content: metaDesc },
        { property: "og:title", content: p.title },
        { property: "og:description", content: longDesc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: shareImage },
        { property: "og:image:secure_url", content: shareImage },
        { property: "og:image:alt", content: p.title },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        ...(published ? [{ property: "article:published_time", content: published }] : []),
        ...(modified ? [{ property: "article:modified_time", content: modified }] : []),
        { property: "article:author", content: author },
        { property: "article:section", content: p.category ?? "Legal News" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: p.title },
        { name: "twitter:description", content: longDesc },
        { name: "twitter:image", content: shareImage },
        { name: "twitter:image:alt", content: p.title },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-narrow py-16 text-center">
      <p className="text-sm text-muted-foreground">Post not found.</p>
      <Link to={"/news" as any} className="mt-3 inline-block text-navy underline">
        Back to news
      </Link>
    </div>
  ),
  errorComponent: () => (
    <div className="container-narrow py-16 text-center">
      <p className="text-sm text-rose-600">Could not load this article.</p>
    </div>
  ),
  component: NewsPostPage,
});

function NewsPostPage() {
  const { post } = Route.useLoaderData() as any;
  useSocialDwellTracker({ kind: "news", itemId: post?.id, itemSlug: post?.slug });
  const fetchFull = useServerFn(getFullNewsContent);
  const [fullState, setFullState] = useState<
    | { status: "idle" }
    | { status: "loading" }
    | { status: "ready"; paragraphs: string[]; sourceTitle?: string | null }
    | { status: "error"; message: string }
  >({ status: "idle" });

  async function loadFull() {
    setFullState({ status: "loading" });
    try {
      const res = await fetchFull({ data: { slug: post.slug } });
      if (res.ok) {
        setFullState({
          status: "ready",
          paragraphs: res.paragraphs,
          sourceTitle: res.sourceTitle,
        });
      } else {
        setFullState({
          status: "error",
          message:
            res.reason === "no-source"
              ? "This story has no linked source to read from."
              : res.reason === "no-content"
              ? "We couldn't extract the full article from the source."
              : "The source didn't respond. Please try again.",
        });
      }
    } catch (e: any) {
      setFullState({ status: "error", message: e?.message ?? "Failed to load" });
    }
  }

  return (
    <article className="container-narrow py-12 max-w-3xl">
      <Link
        to={"/news" as any}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to news
      </Link>
      <p className="mt-4 text-xs uppercase tracking-[0.3em] text-gold">
        {post.category}
      </p>
      <h1 className="mt-1 font-display text-3xl text-navy md:text-4xl">
        {post.title}
      </h1>
      <p className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground">
        <Calendar className="h-3.5 w-3.5" />
        {new Date(post.published_at).toLocaleDateString("en-ZA", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
        {post.source_name && <span>· {post.source_name}</span>}
      </p>
      {post.cover_image_url && (
        <img
          src={post.cover_image_url}
          alt=""
          className="mt-6 w-full rounded-2xl object-cover"
        />
      )}
      {post.excerpt && (
        <p className="mt-6 text-lg leading-relaxed text-foreground/80">
          {post.excerpt}
        </p>
      )}
      {post.body && (() => {
        const { paragraphs, attribution } = splitBodyAndAttribution(post.body);
        const acts = detectLegislation(`${post.title} ${post.body}`);
        return (
          <>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-foreground/90">
              {paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            {acts.length > 0 && (
              <div className="mt-6 rounded-lg border border-gold/30 bg-gold/5 p-4">
                <p className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-navy">
                  <Scale className="h-3.5 w-3.5" /> Legislation referenced
                </p>
                <ul className="flex flex-wrap gap-2">
                  {acts.map((a) => (
                    <li key={a.title}>
                      <Link
                        to={"/legislation" as any}
                        search={{ q: a.short ?? a.title } as any}
                        className="rounded-full border border-navy/20 bg-background px-3 py-1 text-xs text-navy hover:border-navy"
                      >
                        {a.short ?? a.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(attribution || post.source_name) && (
              <p className="mt-6 text-sm text-muted-foreground">
                <span className="font-semibold text-navy">Source:</span>{" "}
                {attribution ?? post.source_name}
              </p>
            )}
          </>
        );
      })()}
      {post.source_url && (
        <div className="mt-8 rounded-2xl border border-navy/15 bg-navy/[0.03] p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-2">
              <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-navy" />
              <div>
                <p className="text-sm font-semibold text-navy">
                  Read the full article — no need to leave this page
                </p>
                <p className="text-xs text-muted-foreground">
                  We fetch the full write-up from the source and display it here for you.
                </p>
              </div>
            </div>
            {fullState.status !== "ready" && (
              <button
                type="button"
                onClick={loadFull}
                disabled={fullState.status === "loading"}
                className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-2 text-xs font-medium text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
              >
                {fullState.status === "loading" ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                  </>
                ) : (
                  <>Load full article</>
                )}
              </button>
            )}
          </div>

          {fullState.status === "ready" && (
            <div className="mt-5">
              {fullState.sourceTitle && (
                <h2 className="font-display text-lg text-navy">
                  {fullState.sourceTitle}
                </h2>
              )}
              <div className="mt-3 space-y-4 text-base leading-relaxed text-foreground/90">
                {fullState.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Fetched from source for readability. Copyright remains with the original publisher.
              </p>
            </div>
          )}

          {fullState.status === "error" && (
            <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
              {fullState.message}
            </div>
          )}

          <a
            href={post.source_url}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-navy"
          >
            View original at source <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      )}


      <div className="mt-10 border-t border-border pt-6">
        <p className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Share this story
        </p>
        <FacebookShareButton
          url={`https://nkadimengdannylcs.com/news/${post.slug}`}
          caption={buildShareCaption(post)}
          kind="news"
          itemId={post.id}
          itemSlug={post.slug}
        />
      </div>
    </article>
  );
}
